import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { RootState } from 'store/store';
import UDTabs from 'ud-ui/components/tabs';
import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { ChampionshipStateData } from 'modules/championship/store/championship/index';
import { stageSelectors } from 'modules/championship/store/stages';
import { useCloseSymbolicTeamForm } from 'modules/championship/ui/hooks/use-close-symbolic-team-form';
import { getChampionshipEditTabs } from './constants';

type ChampionshipEditFormTabsProps = {
  championship?: ChampionshipStateData;
};

const ChampionshipEditFormTabs = ({ championship }: ChampionshipEditFormTabsProps) => {
  const { t } = useTranslation();

  const isTeam = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/:teamId`);
  const isSymbolicTeams = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/symbolic-teams`);

  const isStageCreate = useMatch(Pages.CHAMPIONSHIPS.STEPS.CREATE);
  const stageRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.STAGE}/*`);

  // `/steps/create` тоже подходит под шаблон `:stageId`, поэтому сначала отсекаем
  // создание, а id этапа берём только если это действительно число.
  const routeStageId = Number(stageRoute?.params.stageId);
  const isStageRoute = !isStageCreate && Number.isInteger(routeStageId) && routeStageId > 0;
  // Читаем этап по id из адреса, а не через `selectedStageSelector`: подпись не
  // должна зависеть от того, успел ли стор догнать роут.
  const stage = useSelector((state: RootState) => (isStageRoute ? stageSelectors.selectById(state, routeStageId) : undefined));

  // Тот же колбэк, что гасит форму при уходе с вкладки, — чтобы закрытие по
  // клику на подпись и сброс на размонтировании экрана шли одним путём.
  const closeSymbolicTeamForm = useCloseSymbolicTeamForm(championship?.id);

  let subLabel: string | undefined;
  let urlTab: string | undefined;
  let action = () => {};

  if (isTeam) {
    urlTab = 'applications';
    subLabel = championship?.teams?.find((t) => t.id === Number(isTeam.params.teamId))?.name;
  }

  // Возврат к списку этапов даёт сама навигация по вкладке, поэтому `action` здесь не нужен.
  if (isStageCreate) {
    urlTab = ChampionshipEditTab.STEPS;
    subLabel = t('championship.editTabs.addStageSubLabel');
  } else if (stage) {
    urlTab = ChampionshipEditTab.STEPS;
    subLabel = stage.name;
  }

  if (isSymbolicTeams && championship?.showSymbolicTeamForm) {
    urlTab = ChampionshipEditTab.SYMBOLIC_TEAMS;
    subLabel = championship.selectedSymbolicTeam?.name || t('championship.editTabs.addSymbolicTeamSubLabel');
    action = closeSymbolicTeamForm;
  }

  const tabs = useMemo(() => {
    return getChampionshipEditTabs(t).map((tab) => ({
      ...tab,
      subLabel: urlTab === tab.hash ? subLabel : undefined,
      onClick: urlTab === tab.hash ? action : undefined,
      disabled: !championship,
    }));
    // `action` пересобирается на каждый рендер и в зависимости не идёт: он всегда
    // соответствует текущему `urlTab`, а тот в списке есть.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, urlTab, subLabel, championship]);

  return <UDTabs tabs={tabs} defaultTab={tabs[0]} activeRoutePattern={Pages.CHAMPIONSHIPS.EDIT} />;
};

export default ChampionshipEditFormTabs;
